import PropTypes from 'prop-types';
import { useState, useRef } from 'react';
// @mui
import {
    Grid, Card, Box, Typography, TextField, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// components
import { Iconify } from '../../../components';
// redux
import { dispatch } from '../../../redux/store';
import { changeUserDetails } from '../../../redux/slices/practiceUser';
// Constants
import { socialLink } from '../../../constants/socialLink';

// ----------------------------------------------------------------------

const PLATFORM_META = {
    FACEBOOK: {
        label: 'Facebook',
        icon: 'mdi:facebook',
        color: '#1877F2',
    },
    INSTAGRAM: {
        label: 'Instagram',
        icon: 'mdi:instagram',
        color: '#E4405F',
    },
    TWITTER: {
        label: 'Twitter',
        icon: 'mdi:twitter',
        color: '#000000',
    },
    YOUTUBE: {
        label: 'YouTube',
        icon: 'mdi:youtube',
        color: '#FF0000',
    },
    TIKTOK: {
        label: 'TikTok',
        icon: 'ic:baseline-tiktok',
        color: '#000000',
    },
    LINKEDIN: {
        label: 'LinkedIn',
        icon: 'mdi:linkedin',
        color: '#0A66C2',
    },
    ONLINE_BOOKING: {
        label: 'Online Booking',
        icon: 'mdi:calendar-check',
        color: '#00A76F',
    },
    GOOGLE_BUSINESS_LISTING: {
        label: 'Google Business',
        icon: 'mdi:google-my-business',
        color: '#4285F4',
    },
    GOOGLE_BUSINESS_REVIEW: {
        label: 'Google Reviews',
        icon: 'mdi:google-my-business',
        color: '#4285F4',
    },
};

// ── URL normalisation ─────────────────────────────────────────────────────────

const PLATFORM_BASE_URLS = {
    FACEBOOK: 'https://facebook.com/',
    INSTAGRAM: 'https://instagram.com/',
    TWITTER: 'https://x.com/',
    YOUTUBE: 'https://youtube.com/@',
    TIKTOK: 'https://tiktok.com/@',
    LINKEDIN: 'https://linkedin.com/in/',
};

function normalizeUrl(platformName, value) {
    const trimmed = value.trim();
    if (!trimmed) return trimmed;
    // Already has a protocol — leave it
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    // Handle @handle shorthand
    if (trimmed.startsWith('@')) {
        const base = PLATFORM_BASE_URLS[platformName];
        if (base) {
            const handle = trimmed.slice(1);
            // For YouTube/TikTok the base already ends with @
            return `${base}${handle}`;
        }
    }
    // Default: prepend https://
    return `https://${trimmed}`;
}

// Build the full merged platform list from socialLink constants + any saved values
function buildPlatformList(savedLinks) {
    return socialLink.map((platform) => {
        const saved = savedLinks?.find((s) => s.name === platform.value);
        return {
            name: platform.value,
            label: platform.name,
            value: saved?.value || '',
            id: saved?.id || null,
        };
    });
}

function SocialCard({ platform, onSave, canEdit }) {
    const meta = PLATFORM_META[platform.name] || {
        label: platform.label,
        icon: 'eva:share-fill',
        color: '#64748b',
    };
    const isConnected = Boolean(platform.value);

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(platform.value);
    const inputRef = useRef(null);

    const handleCardClick = () => {
        if (!canEdit) return;
        if (!editing) {
            setDraft(platform.value);
            setEditing(true);
            // Focus on next tick
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    const handleBlur = () => {
        setEditing(false);
        const normalized = normalizeUrl(platform.name, draft.trim());
        setDraft(normalized);
        if (normalized === platform.value) return;
        onSave(platform.name, normalized);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            inputRef.current?.blur();
        } else if (e.key === 'Escape') {
            setDraft(platform.value);
            setEditing(false);
        }
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                p: 2,
                cursor: canEdit ? 'pointer' : 'default',
                border: isConnected
                    ? `1px solid ${alpha(meta.color, 0.3)}`
                    : '1px dashed #e2e8f0',
                bgcolor: isConnected ? alpha(meta.color, 0.04) : '#fff',
                borderRadius: '12px',
                boxShadow: 'none',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': {
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    borderColor: isConnected ? alpha(meta.color, 0.5) : '#cbd5e1',
                },
                position: 'relative',
            }}
        >
            {/* Connected indicator */}
            {isConnected && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#22c55e',
                    }}
                />
            )}

            {/* Icon + Label row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: isConnected ? alpha(meta.color, 0.1) : alpha('#64748b', 0.08),
                    }}
                >
                    <Iconify
                        icon={meta.icon}
                        width={18}
                        sx={{ color: isConnected ? meta.color : '#94a3b8' }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isConnected ? '#1e293b' : '#94a3b8',
                    }}
                >
                    {meta.label}
                </Typography>
            </Box>

            {/* URL or placeholder */}
            {editing ? (
                <TextField
                    inputRef={inputRef}
                    size="small"
                    fullWidth
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={`Enter ${meta.label} URL`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            fontSize: 12,
                            borderRadius: '8px',
                        },
                    }}
                />
            ) : (
                <Typography
                    sx={{
                        fontSize: 12,
                        color: isConnected ? '#475569' : '#cbd5e1',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {isConnected ? platform.value : 'Click to add'}
                </Typography>
            )}
        </Card>
    );
}

SocialCard.propTypes = {
    platform: PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
        id: PropTypes.any,
    }).isRequired,
    onSave: PropTypes.func.isRequired,
    canEdit: PropTypes.bool,
};

SocialCard.defaultProps = {
    canEdit: false,
};

// ----------------------------------------------------------------------

export default function PracticeSocialLinks({ socialLinks, practiceId, canEdit }) {
    const [platforms, setPlatforms] = useState(() => buildPlatformList(socialLinks));

    const connectedCount = platforms.filter((p) => Boolean(p.value)).length;

    const handleSave = (platformName, newValue) => {
        const updated = platforms.map((p) =>
            p.name === platformName ? { ...p, value: newValue } : p
        );
        setPlatforms(updated);

        // Persist to API
        const data = updated
            .filter((p) => p.name && p.value)
            .map((p) => {
                const entry = { name: p.name, value: p.value };
                if (p.id) entry.id = p.id;
                return entry;
            });
        dispatch(changeUserDetails({ social_medias: data }, practiceId, 'social'));
    };

    const badge = (
        <Chip
            label={`${connectedCount} of ${platforms.length} connected`}
            size="small"
            sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                bgcolor: connectedCount > 0 ? alpha('#22c55e', 0.1) : alpha('#64748b', 0.08),
                color: connectedCount > 0 ? '#16a34a' : '#64748b',
                '& .MuiChip-label': { px: 1 },
            }}
        />
    );

    return (
        <Card
            id="social-links"
            sx={{ p: 0, overflow: 'hidden', borderRadius: 2, boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)' }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.75,
                    bgcolor: alpha('#8b5cf6', 0.04),
                    borderBottom: `1px solid ${alpha('#8b5cf6', 0.08)}`,
                }}
            >
                <Box
                    sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha('#8b5cf6', 0.1),
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="eva:share-fill" width={16} sx={{ color: '#8b5cf6' }} />
                </Box>
                <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontSize={14} fontWeight={700} color="#1e293b">
                            Social Links
                        </Typography>
                        {badge}
                    </Box>
                    <Typography fontSize={12} color="#94a3b8" noWrap>
                        Connect your practice social media profiles
                    </Typography>
                </Box>
            </Box>

            {/* Card grid */}
            <Box sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                    {platforms.map((platform) => (
                        <Grid
                            item
                            key={platform.name}
                            xs={12}
                            sm={6}
                            md={4}
                        >
                            <SocialCard platform={platform} onSave={handleSave} canEdit={canEdit} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Card>
    );
}

PracticeSocialLinks.propTypes = {
    socialLinks: PropTypes.array,
    practiceId: PropTypes.any,
    canEdit: PropTypes.bool,
};

PracticeSocialLinks.defaultProps = {
    socialLinks: [],
    practiceId: null,
    canEdit: false,
};
